<template>
  <div>
    <Toasts />
    <GuidedTour />

    <div ref="app" class="layout">
      <!-- Below `sm` the sidebar floats over the content instead of taking a
           column from it, so it becomes a modal surface: the scrim stops the
           content behind it being clickable, and `Sidebar` traps focus while
           it is open. -->
      <PanelOverlayScrim
        :open="col1IsOverlay"
        @dismiss="closeCol1Overlay"
      ></PanelOverlayScrim>

      <!-- The one control that reopens a hidden sidebar, and the reason Overlay
           could not ship before: it has to live *outside* the panel, or hiding
           the panel takes the way back with it. -->
      <button
        v-if="col1IsHidden"
        type="button"
        class="layout__open-nav"
        :aria-label="$t('sidebar.openNavigation')"
        @click="openCol1Overlay"
      >
        <i class="baserow-icon-sidebar" aria-hidden="true"></i>
      </button>

      <div
        class="layout__col-1"
        :class="{ 'layout__col-1--overlay': col1IsOverlay }"
        :style="{ '--layout-col-1-width': col1EffectiveWidth + 'px' }"
      >
        <Sidebar
          :workspaces="workspaces"
          :selected-workspace="selectedWorkspace"
          :applications="applications"
          :collapsed="col1Collapsed"
          :overlay="col1IsOverlay"
          :toggle-actionable="col1ToggleActionable"
          :width="col1Width"
          :right-sidebar-open="col3Visible"
          :forced-by-views="col1ForcedByViews && !col1Narrow"
          @set-col1-collapsed="setCol1Collapsed"
          @dismiss-overlay="closeCol1Overlay"
          @open-workspace-search="openWorkspaceSearch"
        />
      </div>

      <div
        class="layout__col-2"
        :style="{
          '--layout-col-1-width': col1EffectiveWidth + 'px',
          '--layout-col-3-width': col3Visible ? col3EffectiveWidth + 'px' : 0,
        }"
      >
        <slot />
      </div>

      <div
        v-if="col3Visible"
        class="layout__col-3"
        :style="{ width: col3EffectiveWidth + 'px', right: 0 }"
      >
        <RightSidebar :workspace="selectedWorkspace" />
      </div>

      <!-- Below `md` the sidebar is a rail whose width the user does not
           choose, so the drag handle is hidden by CSS rather than removed
           here: whether the handle applies is a viewport question. -->
      <HorizontalResize
        v-if="!col1Collapsed"
        class="layout__resize layout__resize--col-1"
        :width="col1Width"
        :style="{ '--layout-col-1-width': col1Width + 'px' }"
        :min="COL1_MIN_WIDTH"
        :max="COL1_MAX_WIDTH"
        @move="resizeCol1"
      />

      <HorizontalResize
        v-if="col3Visible"
        class="layout__resize layout__resize--col-3"
        :width="col3EffectiveWidth"
        :style="{ right: col3EffectiveWidth - 3 + 'px' }"
        :min="COL3_MIN_WIDTH"
        :max="COL3_MAX_WIDTH"
        :right="true"
        @move="resizeCol3"
      />

      <component
        :is="component"
        v-for="(component, index) in appLayoutComponents"
        :key="index"
      />
    </div>

    <WorkspaceSearchModal ref="workspaceSearchModal" />
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onBeforeUnmount, watch } from 'vue'
import { useStore } from 'vuex'

import Toasts from '@baserow/modules/core/components/toasts/Toasts.vue'
import Sidebar from '@baserow/modules/core/components/sidebar/Sidebar.vue'
import PanelOverlayScrim from '@baserow/modules/core/components/sidebar/PanelOverlayScrim.vue'
import RightSidebar from '@baserow/modules/core/components/sidebar/RightSidebar.vue'
import HorizontalResize from '@baserow/modules/core/components/HorizontalResize.vue'
import GuidedTour from '@baserow/modules/core/components/guidedTour/GuidedTour.vue'
import WorkspaceSearchModal from '@baserow/modules/core/components/workspace/WorkspaceSearchModal.vue'
import { CORE_ACTION_SCOPES } from '@baserow/modules/core/utils/undoRedoConstants'
import {
  isOsSpecificModifierPressed,
  keyboardShortcutsToPriorityEventBus,
} from '@baserow/modules/core/utils/events'
import { notifyIf } from '@baserow/modules/core/utils/error'
import {
  readLocalStorage,
  readLocalStorageBoolean,
  writeLocalStorage,
  SHELL_LEFT_COLLAPSED_KEY,
  SHELL_LEFT_WIDTH_KEY,
  SHELL_RIGHT_OPEN_KEY,
  SHELL_RIGHT_WIDTH_KEY,
} from '@baserow/modules/core/utils/localStorage'
import { BREAKPOINT_MD, below } from '@baserow/modules/core/utils/breakpoints'
import {
  LEFT_NAV_SIDEBAR,
  LEFT_NAV_VIEWS,
} from '@baserow/modules/core/store/shell'
import {
  BAND_WIDE,
  PANEL_HIDDEN,
  PANEL_OVERLAY,
  panelIsIconOnly,
  panelToggleIsActionable,
} from '@baserow/modules/core/utils/shellPanelState'

const store = useStore()
const { $registry, $priorityBus, $realtime, $bus } = useNuxtApp()

// Collapse and width are independent state. Collapse is a boolean the user sets
// with the toggle; width is a number the user sets by dragging. The drag
// minimum sits well above the collapsed width so that a drag can never land in
// a width that merely looks collapsed -- that ambiguity is what made the old
// `col1Width < 170` derivation unable to tell "make this narrower" apart from
// "collapse this".
const COL1_DEFAULT_WIDTH = 240
const COL1_MIN_WIDTH = 180
const COL1_MAX_WIDTH = 300
// Both rails are the same width. They sit directly against each other when both
// are collapsed, so 52 next to 40 read as a mistake rather than a distinction --
// two columns of the same 32px icons, in columns 12px apart. 52 is the one that
// can hold a 32px control with symmetric clearance (52 - 1px border = 51px
// content box, 9.5px either side); 40 left only 3.5px and is why the views
// rail's own rows overflowed it. Keep in step with `$views-panel-collapsed-width`
// in `variables.scss`, which sizes the same rail from the stylesheet side.
const RAIL_COLLAPSED_WIDTH = 52
const COL1_COLLAPSED_WIDTH = RAIL_COLLAPSED_WIDTH
const VIEWS_PANEL_EXPANDED_WIDTH = 240
const VIEWS_PANEL_COLLAPSED_WIDTH = RAIL_COLLAPSED_WIDTH

function readCol1Width() {
  const stored = parseInt(readLocalStorage(SHELL_LEFT_WIDTH_KEY), 10)
  if (isNaN(stored)) {
    return COL1_DEFAULT_WIDTH
  }
  // Clamp rather than reject: a stored width from an older min/max is still a
  // usable preference, it just needs bringing into range.
  return Math.min(Math.max(stored, COL1_MIN_WIDTH), COL1_MAX_WIDTH)
}

// These four refs hold *shell geometry* the user has stored in `localStorage`,
// and they deliberately start at their defaults rather than at the stored
// value.
//
// This component is server-rendered, and the server has no `localStorage`: it
// always produced the default (expanded, 240px) while the client immediately
// produced whatever was stored. Vue hydrates against the server's markup, so a
// user who had collapsed the sidebar got "Hydration completed but contains
// mismatches" in the console -- and, far worse, a shell that had adopted the
// *collapsed 52px column* while still rendering the *expanded* sidebar inside
// it. Every control spilled out of the 52px rail: the search field, the Ctrl+K
// chip, the member count and both footer buttons all overhung the edge.
//
// `applyStoredShellGeometry()` re-reads them in `onMounted`, which only ever
// runs on the client and only after hydration has matched.
const col1Width = ref(COL1_DEFAULT_WIDTH)

// What the user chose, independent of viewport. Kept separate from
// `col1Collapsed` below so that a narrow viewport can force the rail without
// overwriting the preference -- widening the window has to give the user back
// the sidebar they actually asked for.
const col1CollapsedPreference = ref(false)

// True while the viewport is below `$breakpoint-md`. `05-sidebar-system.md`
// §3.3 lists "viewport in [--shell-bp-sm, --shell-bp-md)" as a trigger for
// Collapsed-to-rail in its own right, so inside that band the sidebar is a rail
// regardless of the preference.
// Derived from the band the store already resolves, rather than a second
// `matchMedia` ref tracking the same boundary. Two sources for one fact is the
// shape this whole re-architecture exists to remove; this one survived Phase 3
// because it is read for things other than the panel state -- the right
// region's width budget, and whether a collapse is worth persisting.
const col1Narrow = computed(
  () => store.getters['shell/viewportBand'] !== BAND_WIDE
)

// True while the views rail holds the left navigation budget (Model C,
// `10-placement-proposal.md` §4.3). Like `col1Narrow` this is a *force*, not a
// preference: it is a reason the sidebar is currently a rail, and it is
// deliberately kept out of `col1CollapsedPreference` so that releasing the
// budget gives the user back whatever they last chose.
//
// It has no effect inside the narrow band, because `col1Narrow` already forces
// the rail there. Model C is a no-op below `$breakpoint-md` rather than a
// second, competing rule.
const col1ForcedByViews = computed(() =>
  store.getters['shell/isLeftNavForcedCollapsed'](LEFT_NAV_SIDEBAR)
)

// Settled during setup rather than in `onMounted`, because `col1Collapsed`
// is read on the first render: hydrating a frame later would paint the sidebar
// as a rail and then widen it. This matches how `col1CollapsedPreference` and
// `col1Width` above already read storage during setup.
//
// The fallback only applies on a browser that has never stored a holder --
// every existing user, once. The sidebar claims it if the user's own
// preference says expanded, which reproduces today's default (state L1).
store.dispatch(
  'shell/hydrateLeftNav',
  col1CollapsedPreference.value ? null : LEFT_NAV_SIDEBAR
)

// The geometry that follows from this is CSS (`layout.scss`). This flag exists
// only because the rail is not purely a width: `Sidebar.vue` hides the
// workspace name, the tree labels and the collapse toggle with `v-show`, which
// no stylesheet can reach. So the viewport decides the *state*, and the state
// is what JS holds.
//
// Resolved by `store/shell.js` rather than combined here. The same three inputs
// -- viewport band, navigation budget, preference -- used to be OR'd together in
// this file and again in `Table.vue`, two copies that could not see each other;
// `docs/hypedrive/17-shell-rearchitecture.md` §1 has what that cost.
const col1PanelState = computed(() =>
  store.getters['shell/panelState'](LEFT_NAV_SIDEBAR)
)

// See `panelIsIconOnly` for why this is not simply "not expanded": that
// definition used to also catch `overlay`, which measured live at 52px,
// icons only -- the one state meant to show the full sidebar on a phone.
const col1Collapsed = computed(() => panelIsIconOnly(col1PanelState.value))

// Below `sm`, where the sidebar floats over the content rather than taking a
// column. `05-sidebar-system.md` §3.3 -- specified since the shell was designed,
// unreachable until the resolver landed, because the state was computed as
// `narrow && !collapsed` beside `collapsed = narrow || …`.
const col1IsOverlay = computed(() => col1PanelState.value === PANEL_OVERLAY)

const col1IsHidden = computed(() => col1PanelState.value === PANEL_HIDDEN)

// In `[sm, md)` the rail is forced by the viewport, so a collapse toggle there
// cannot do anything. It used to render regardless: measured at 1199px, the
// click fired, `aria-expanded` flipped, and nothing moved. A control that
// announces a state change it did not make is worse than one that is absent.
const col1ToggleActionable = computed(() =>
  panelToggleIsActionable(
    col1PanelState.value,
    store.getters['shell/viewportBand']
  )
)

function setCol1Overlay(open) {
  store.dispatch('shell/setOverlayOpen', { panel: LEFT_NAV_SIDEBAR, open })
}

function openCol1Overlay() {
  setCol1Overlay(true)
}

function closeCol1Overlay() {
  setCol1Overlay(false)
}
// Mirrors `readCol1Width`. The bounds are the ones the `HorizontalResize` in
// the template enforces while dragging, so a stored value can never restore the
// panel to a width the user could not have dragged it to.
const COL3_DEFAULT_WIDTH = 400
const COL3_MIN_WIDTH = 300
const COL3_MAX_WIDTH = 500

function readCol3Width() {
  const stored = parseInt(readLocalStorage(SHELL_RIGHT_WIDTH_KEY), 10)
  if (isNaN(stored)) {
    return COL3_DEFAULT_WIDTH
  }
  // Clamp rather than reject, for the same reason as col1: a width stored under
  // older bounds is still a real preference and only needs bringing into range.
  return Math.min(Math.max(stored, COL3_MIN_WIDTH), COL3_MAX_WIDTH)
}

const col3Width = ref(COL3_DEFAULT_WIDTH)
// Absent means closed. A 400px panel is chrome the user has to ask for, so it
// is opt-in rather than opt-out.
const col3Visible = ref(false)

/**
 * Adopts the stored shell geometry. Called from `onMounted`, never during
 * setup: reading `localStorage` while rendering makes the server and the client
 * disagree, and the shell is the one place where that disagreement is visible
 * as a broken layout rather than a console warning.
 */
function applyStoredShellGeometry() {
  col1Width.value = readCol1Width()
  col1CollapsedPreference.value = readLocalStorageBoolean(
    SHELL_LEFT_COLLAPSED_KEY
  )
  store.dispatch('shell/setCollapsedPreference', {
    panel: LEFT_NAV_SIDEBAR,
    collapsed: col1CollapsedPreference.value,
  })
  col3Width.value = readCol3Width()
  col3Visible.value = readLocalStorageBoolean(SHELL_RIGHT_OPEN_KEY)
}
const app = ref()

const workspaceSearchModal = ref(null)

const workspaces = computed(() => store.getters['workspace/getAll'])
const selectedWorkspace = computed(() => store.getters['workspace/getSelected'])
const applications = computed(() => store.getters['application/getAll'])

// Width is only consulted when not collapsed, so collapsing preserves the
// user's chosen width instead of overwriting it with a magic number.
const col1EffectiveWidth = computed(() =>
  col1Collapsed.value ? COL1_COLLAPSED_WIDTH : col1Width.value
)

// Stored widths are restored independently, so an older session can contain
// both panels at values that fit individually but not together. Keep the left
// navigation stable and reduce the contextual right panel only for rendering;
// the preference itself remains intact and returns as soon as the viewport has
// enough room again.
const col3EffectiveWidth = computed(() => {
  if (typeof window === 'undefined' || col1Narrow.value) {
    return col3Width.value
  }

  const available =
    window.innerWidth -
    col1EffectiveWidth.value -
    viewsPanelOccupiedWidth.value -
    CONTENT_MIN_WIDTH
  return Math.max(Math.min(col3Width.value, available), 0)
})

const viewsPanelOccupiedWidth = computed(() => {
  if (
    store.getters['shell/dualLeftNavigationAllowed'] ||
    store.getters['shell/leftNavExpanded'] === LEFT_NAV_VIEWS
  ) {
    return VIEWS_PANEL_EXPANDED_WIDTH
  }
  return VIEWS_PANEL_COLLAPSED_WIDTH
})

const route = useRoute()
const router = useRouter()

// Preserve authentication logic
if (route.query.token) {
  const newQuery = { ...route.query }
  delete newQuery.token
  router.replace({ query: newQuery })
}

function openWorkspaceSearch() {
  if (selectedWorkspace.value && workspaceSearchModal.value) {
    workspaceSearchModal.value.show()
  }
}

// The narrowest the content column is allowed to become. Each resize handle
// clamps only its own bounds, independently of the other, so without a shared
// floor the maxima compose: at 1280px a 300px sidebar plus a 240px views rail
// plus a 500px right region leaves 240px of grid -- narrower than a single
// default field column, and reachable by dragging two handles that each
// believe they are within range.
//
// 480px is two default 180px columns plus the row-number gutter, i.e. the
// point below which the grid stops being a grid. The floor is applied to the
// region being dragged rather than to the other one, so a drag stops early
// instead of yanking a panel the user is not touching.
const CONTENT_MIN_WIDTH = 480

function updateDualLeftNavigationCapacity() {
  if (typeof window === 'undefined') {
    return
  }

  const requiredWidth =
    col1Width.value +
    VIEWS_PANEL_EXPANDED_WIDTH +
    (col3Visible.value ? col3Width.value : 0) +
    CONTENT_MIN_WIDTH
  const allowed = window.innerWidth >= requiredWidth

  if (store.getters['shell/dualLeftNavigationAllowed'] !== allowed) {
    store.dispatch('shell/setDualLeftNavigationAllowed', allowed)
  }
}

watch([col1Width, col3Width, col3Visible], updateDualLeftNavigationCapacity)

/**
 * The widest this region may be dragged before the content column would fall
 * below its floor. `otherWidth` is whatever the opposite region currently
 * occupies; the views rail is not included because it lives in `Table.vue` and
 * is not visible from here -- see the `shell` store note in doc 05.
 */
function widthCeiling(hardMax, otherWidth) {
  if (typeof window === 'undefined') {
    return hardMax
  }
  const available = window.innerWidth - otherWidth - CONTENT_MIN_WIDTH
  return Math.max(Math.min(hardMax, available), 0)
}

// Dragging sets the width and never sets `collapsed`.
function resizeCol1(v) {
  const ceiling = widthCeiling(
    COL1_MAX_WIDTH,
    col3Visible.value ? col3EffectiveWidth.value : 0
  )
  if (ceiling < COL1_MIN_WIDTH) {
    return
  }
  const capped = Math.min(v, ceiling)
  col1Width.value = capped
  writeLocalStorage(SHELL_LEFT_WIDTH_KEY, capped)
}
function resizeCol3(v) {
  const ceiling = widthCeiling(COL3_MAX_WIDTH, col1EffectiveWidth.value)
  if (ceiling < COL3_MIN_WIDTH) {
    return
  }
  const capped = Math.min(v, ceiling)
  col3Width.value = capped
  writeLocalStorage(SHELL_RIGHT_WIDTH_KEY, capped)
}

// Collapsing sets the boolean and never writes the width. Inside the narrow
// band the sidebar is a rail no matter what, so a toggle there would not change
// anything visible and is not worth recording as a preference.
function setCol1Collapsed(value) {
  // Below `sm` the sidebar has no column to collapse into -- it is hidden or
  // floating -- and `resolvePanelState` decides that band on `overlayOpen`
  // alone, never on the preference. So writing a preference here would be the
  // exact no-op the comment below describes fixing for the narrow band: the
  // toggle renders, `aria-expanded` flips, and the panel does not move. The
  // toggle instead closes the overlay, which is the same gesture meaning the
  // same thing at a width where a docked sidebar does not fit. `Table.vue`'s
  // `setViewsPanelCollapsed` has done this for the views rail all along.
  if (col1IsOverlay.value || col1IsHidden.value) {
    setCol1Overlay(value === false)
    return
  }

  // No longer returns early inside the narrow band. It used to, which made the
  // toggle a no-op that still flipped `aria-expanded` -- measured at 1199px:
  // the handler ran, the class did not move, and storage was never written.
  // The band is the resolver's business now, so recording the preference here
  // is always correct: it is what the user asked for, and it is what they get
  // back the moment the viewport allows it.
  col1CollapsedPreference.value = value
  store.dispatch('shell/setCollapsedPreference', {
    panel: LEFT_NAV_SIDEBAR,
    collapsed: value,
  })

  // Still not written inside the band, for the original reason: a rail forced
  // by the viewport is not a choice, and persisting it would outlive the
  // viewport that caused it.
  if (!col1Narrow.value) {
    writeLocalStorage(SHELL_LEFT_COLLAPSED_KEY, value)
  }

  // Model C. Expanding claims the left navigation budget, which forces the
  // views rail collapsed; collapsing only gives the budget up, and pointedly
  // does not hand it to the rail. That asymmetry is what makes "both collapsed"
  // (state L3) a state the user can reach and stay in.
  store.dispatch(
    value ? 'shell/releaseLeftNav' : 'shell/claimLeftNav',
    LEFT_NAV_SIDEBAR
  )
}

function toggleRightSidebar(value = !col3Visible.value) {
  col3Visible.value = value
  writeLocalStorage(SHELL_RIGHT_OPEN_KEY, col3Visible.value)
}

function keyDown(event) {
  if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === 'k') {
    event.preventDefault()
    openWorkspaceSearch()
    return
  }

  if (isOsSpecificModifierPressed(event) && event.key.toLowerCase() === 'z') {
    const el = document.activeElement
    const avoid =
      ['input', 'textarea', 'select'].includes(el.tagName.toLowerCase()) ||
      el.isContentEditable

    if (!avoid) {
      const actionName = event.shiftKey ? 'undoRedo/redo' : 'undoRedo/undo'
      store.dispatch(actionName, { showLoadingToast: true }).catch(notifyIf)
      event.preventDefault()
    }
  }

  keyboardShortcutsToPriorityEventBus(event, $priorityBus)
}

// `matchMedia` rather than a `resize` listener: it fires only when the band is
// actually crossed, so nothing runs during the rest of a drag.
let col1NarrowQuery = null
// `matchMedia` fires only when a boundary is crossed, unlike `resize`, so this
// is the cheap signal; `setViewportWidth` derives the three-way band from the
// real width, because one query cannot report two boundaries.
function onCol1NarrowChange() {
  store.dispatch('shell/setViewportWidth', window.innerWidth)
}

function updateViewportBand() {
  store.dispatch('shell/setViewportWidth', window.innerWidth)
}

onMounted(() => {
  applyStoredShellGeometry()
  updateViewportBand()

  col1NarrowQuery = window.matchMedia(below(BREAKPOINT_MD))
  onCol1NarrowChange(col1NarrowQuery)
  col1NarrowQuery.addEventListener('change', onCol1NarrowChange)
  // `col1NarrowQuery` only fires on the `md` boundary, so the `sm` one would go
  // unnoticed. `updateDualLeftNavigationCapacity` is already bound to `resize`
  // below and re-derives the band with it.
  window.addEventListener('resize', updateViewportBand)
  updateDualLeftNavigationCapacity()
  window.addEventListener('resize', updateDualLeftNavigationCapacity)

  $realtime.connect()

  const handler = (e) => keyDown(e)
  document.body.addEventListener('keydown', handler)
  //nuxtApp.$el = { keydownEvent: handler }
  app.value.keydownEvent = handler

  store.dispatch('undoRedo/updateCurrentScopeSet', CORE_ACTION_SCOPES.root())

  store.dispatch('job/initializePoller')

  $bus.$on('toggle-right-sidebar', toggleRightSidebar)
})

onBeforeUnmount(() => {
  col1NarrowQuery?.removeEventListener('change', onCol1NarrowChange)
  window.removeEventListener('resize', updateDualLeftNavigationCapacity)
  window.removeEventListener('resize', updateViewportBand)

  $realtime.disconnect()

  if (app.value?.keydownEvent) {
    document.body.removeEventListener('keydown', app.value?.keydownEvent)
  }

  store.dispatch(
    'undoRedo/updateCurrentScopeSet',
    CORE_ACTION_SCOPES.root(false)
  )

  $bus.$off('toggle-right-sidebar', toggleRightSidebar)
})

const appLayoutComponents = computed(() => {
  return Object.values($registry.getAll('plugin'))
    .map((plugin) => plugin.getAppLayoutComponent())
    .filter((component) => component !== null)
})
</script>
